// `…/email-signin` — "Please connect your work email", in a side view.
//
// ⚠️ ONE COMPONENT, FOUR STATES, AND THEY DIFFER IN EXACTLY TWO THINGS: whether `alert-id` is
// bound (which is what puts the ✕ there) and which state the ✕ closes to. The review screens
// pass it; the profile screens do not bind it at all, because there the panel is the right half
// of the screen rather than an overlay on a list. So this route takes the close target as a
// prop and reads the alert id from the URL — present on the review routes, absent on the
// profile ones, which reproduces the difference without a second component.
import { useEffect, useState, type ReactElement } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { emailIntegrationConnectUrl, fetchViewModel, validateAndSaveExchange } from '@woo-ext/api'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import { EmailSignIn, parseEmailServerType, type EmailServerType } from '@woo-ext/ui'
import { isMailboxConnected, PostEventType, publishPostEvent, subscribePostEvents } from '../postChannel.ts'

export interface EmailSignInRouteProps {
  /** Where the ✕ goes, with the alert id re-attached. Absent means no ✕ is rendered anyway. */
  closePath?: string
}

export function EmailSignInRoute({ closePath }: EmailSignInRouteProps): ReactElement {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [forceType, setForceType] = useState<EmailServerType | undefined>(undefined)

  const rawAlertId = params.get('alertId')
  const alertId = rawAlertId === null ? undefined : Number.parseInt(rawAlertId, 10)
  const emailServerType = parseEmailServerType(params.get('emailServerType'))

  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (!live) return
        // `careero.forceEmailServerType` — a tenant that has picked one provider for everybody.
        // It is the only thing that can put the Exchange button on screen.
        const raw = model.forceEmailServerType
        setForceType(typeof raw === 'number' && raw >= 0 ? (raw as EmailServerType) : undefined)
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])

  /**
   * `setupOnEmailSignin` — once the mailbox is connected, go on to what the user was doing: the
   * composer beside this panel (`messageCandidates` / `CandidateProfileCandidateMessages`, and the
   * contacts' equivalents). The four panels are each a sibling of their composer, so the move is
   * the last path segment, with the alert id and the other params kept.
   *
   * Upstream also checks that the user is still on the sign-in state; here the subscription ends
   * when this panel unmounts, which is the same condition.
   */
  useEffect(
    () =>
      subscribePostEvents((message) => {
        if (!isMailboxConnected(message)) return
        const composer = pathname.includes('/client-contact/') ? 'client-contact-messages' : 'candidate-messages'
        const query = new URLSearchParams(params)
        query.delete('emailServerType')
        const search = query.toString()
        void navigate(`${pathname.replace(/\/email-signin$/, `/${composer}`)}${search === '' ? '' : `?${search}`}`)
      }),
    [navigate, params, pathname],
  )

  return (
    <EmailSignIn
      {...(alertId === undefined || Number.isNaN(alertId) || closePath === undefined ? {} : { alertId })}
      {...(emailServerType === undefined ? {} : { emailServerType })}
      {...(forceType === undefined ? {} : { forceEmailServerType: forceType })}
      flexBasis={ExtensionViewWidth.Extended}
      onClose={() => {
        if (closePath === undefined) return
        void navigate(
          alertId === undefined || Number.isNaN(alertId) ? closePath : `${closePath}?alertId=${String(alertId)}`,
        )
      }}
      onConnect={(provider) => {
        // `getConnectionUrlWithParams` — with no alert behind it, the round-trip carries
        // `phase=EMAIL_SIGNUP`, which is what tells the tenant this is onboarding rather than a
        // message being composed. Exchange opens a modal instead and lands with that modal.
        //
        // ⚠️ BUILT BY `emailIntegrationConnectUrl`, NOT BY HAND. The hand-built URL carried no
        // `origin`, so careero's resolver fell back to woo.io and the post page's BroadcastChannel
        // never reached this SPA: the mailbox connected and the panel never knew.
        if (provider === 'exchange') return
        const withAlert = alertId !== undefined && !Number.isNaN(alertId)
        window.open(
          emailIntegrationConnectUrl(provider === 'gmail' ? 'google' : 'outlook', withAlert ? {} : { phase: 'EMAIL_SIGNUP' }),
          '_blank',
        )
      }}
      onExchangeSignIn={async (username, password) =>
        (await validateAndSaveExchange({ username, password })).success
      }
      // The dialog's broadcast, which this same panel hears and acts on (see above).
      onExchangeConnected={() => {
        publishPostEvent(PostEventType.ExchangeConnected)
      }}
    />
  )
}
